import { z } from "zod";

import { conductorMaterialSchema, jurisdictionProfileSchema } from "../services/cableContract";
import {
  circuitKindSchema,
  deviceKindSchema,
  mcbTypeSchema,
  operatingCurrentBasisSchema,
  supplyKindSchema,
  systemKindSchema,
  type CircuitKind,
  type DeviceKind,
  type McbType,
  type OperatingCurrentBasis,
  type SupplyKind,
  type SystemKind,
} from "../services/earthFaultLoopContract";
import type { ValidationLabels } from "../utils/validationMessages";

// Draft model of the earth-fault loop form (EOS-05d, A33): what the user is typing, kept
// apart from the components so the payload rules are tested without a browser.
//
// Every value is held as the string the user typed. Ze comes from one of three places (D3):
// a stored EOS-04 earth-fault run of the active revision (LINKED, the default), a declared
// value with its source (DECLARED), or none (NOT_ESTABLISHED: nothing is compared). Each
// row's cable comes from a stored EOS-06 cable run (LINKED, the default) or typed figures
// (TYPED). A blank optional item (a source, the time, Ia, Zs_max, the temperature, the
// nominal Uo, C_min) is left OUT of the payload: blank means NOT ESTABLISHED, and the engine
// must see it as absent, never as "". A required figure is sent as "" so the schema names it
// by its on-screen label; a choice the user has not made ("" = Choose...) is left out so the
// schema reports it as required.
//
// Rows are separate entries with stable ids, so adding or removing one never disturbs the
// others; the ids never reach the backend.

/** The module key the draft is kept under in the browser tab (see useStudyDraft). */
export const EARTH_FAULT_LOOP_DRAFT_MODULE = "earth-fault-loop";

/** Where Ze comes from, on screen. */
export const EXTERNAL_IMPEDANCE_SOURCES = ["LINKED", "DECLARED", "NONE"] as const;
export type ExternalImpedanceSourceChoice = (typeof EXTERNAL_IMPEDANCE_SOURCES)[number];

/** Where a row's cable figures come from, on screen. */
export const CABLE_SOURCES = ["LINKED", "TYPED"] as const;
export type CableSourceChoice = (typeof CABLE_SOURCES)[number];

export type CircuitRowDraft = {
  id: string;
  code: string;
  name: string;
  circuitKind: string;
  cableSource: CableSourceChoice;
  cableRunId: string;
  lineConductorAreaMm2: string;
  protectiveConductorAreaMm2: string;
  conductorMaterial: string;
  routeLengthM: string;
  parallelRuns: string;
  protectiveConductorRuns: string;
  cableSourceText: string;
  lineResistanceOhmPerKm20c: string;
  protectiveResistanceOhmPerKm20c: string;
  resistanceSource: string;
  reactanceOhmPerKm: string;
  reactanceSource: string;
  conductorTemperatureC: string;
  temperatureSource: string;
  deviceCode: string;
  deviceKind: string;
  mcbType: string;
  ratedCurrentA: string;
  ratedVoltageV: string;
  characteristicText: string;
  requiredDisconnectionTimeS: string;
  timeSource: string;
  operatingCurrentA: string;
  operatingCurrentSource: string;
  maximumLoopImpedanceOhm: string;
  maximumLoopImpedanceSource: string;
  operatingCurrentBasis: string;
  minimumMarginPercent: string;
  sldElementCode: string;
};

export type EarthFaultLoopDraft = {
  code: string;
  name: string;
  busCode: string;
  systemKind: string;
  supplyKind: string;
  systemNominalVoltageV: string;
  frequencyHz: string;
  externalImpedanceSource: ExternalImpedanceSourceChoice;
  faultRunId: string;
  declaredExternalImpedanceOhm: string;
  declaredExternalImpedanceSource: string;
  minimumVoltageFactor: string;
  voltageFactorSource: string;
  nominalLineToEarthVoltageV: string;
  nominalVoltageSource: string;
  rows: CircuitRowDraft[];
  jurisdictionProfile: string;
  sldElementCode: string;
  notes: string;
};

// A choice the user has not made yet is held as "".
function chosenOrBlank<T extends z.ZodType<string>>(choice: T) {
  return z.union([z.literal(""), choice]);
}

const circuitRowDraftSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    circuitKind: chosenOrBlank(circuitKindSchema),
    cableSource: z.enum(CABLE_SOURCES),
    cableRunId: z.string(),
    lineConductorAreaMm2: z.string(),
    protectiveConductorAreaMm2: z.string(),
    conductorMaterial: chosenOrBlank(conductorMaterialSchema),
    routeLengthM: z.string(),
    parallelRuns: z.string(),
    protectiveConductorRuns: z.string(),
    cableSourceText: z.string(),
    lineResistanceOhmPerKm20c: z.string(),
    protectiveResistanceOhmPerKm20c: z.string(),
    resistanceSource: z.string(),
    reactanceOhmPerKm: z.string(),
    reactanceSource: z.string(),
    conductorTemperatureC: z.string(),
    temperatureSource: z.string(),
    deviceCode: z.string(),
    deviceKind: chosenOrBlank(deviceKindSchema),
    mcbType: chosenOrBlank(mcbTypeSchema),
    ratedCurrentA: z.string(),
    ratedVoltageV: z.string(),
    characteristicText: z.string(),
    requiredDisconnectionTimeS: z.string(),
    timeSource: z.string(),
    operatingCurrentA: z.string(),
    operatingCurrentSource: z.string(),
    maximumLoopImpedanceOhm: z.string(),
    maximumLoopImpedanceSource: z.string(),
    operatingCurrentBasis: chosenOrBlank(operatingCurrentBasisSchema),
    minimumMarginPercent: z.string(),
    sldElementCode: z.string(),
  })
  .strict();

// The shape a draft kept in the browser tab must still have to be restored: exactly the
// fields above. A draft saved by an older form fails here and is dropped instead of
// half-filling this one.
export const earthFaultLoopDraftSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    busCode: z.string(),
    systemKind: chosenOrBlank(systemKindSchema),
    supplyKind: chosenOrBlank(supplyKindSchema),
    systemNominalVoltageV: z.string(),
    frequencyHz: z.string(),
    externalImpedanceSource: z.enum(EXTERNAL_IMPEDANCE_SOURCES),
    faultRunId: z.string(),
    declaredExternalImpedanceOhm: z.string(),
    declaredExternalImpedanceSource: z.string(),
    minimumVoltageFactor: z.string(),
    voltageFactorSource: z.string(),
    nominalLineToEarthVoltageV: z.string(),
    nominalVoltageSource: z.string(),
    rows: z.array(circuitRowDraftSchema).min(1),
    jurisdictionProfile: jurisdictionProfileSchema,
    sldElementCode: z.string(),
    notes: z.string(),
  })
  .strict();

let nextRowNumber = 1;

// A prefix of its own, so a circuit row id is never mistaken for another form's.
export function createCircuitRowId(): string {
  const id = `circuit-${nextRowNumber}`;
  nextRowNumber += 1;
  return id;
}

// Row ids come from a counter that starts again at 1 on every page load, while a restored
// draft brings the ids it was saved with. Moving the counter past every id in use keeps a
// row added afterwards from taking the id of one already on screen.
export function seedCircuitRowIdCounter(ids: readonly string[]): void {
  for (const id of ids) {
    const match = /^circuit-(\d+)$/.exec(id);
    if (match !== null) {
      nextRowNumber = Math.max(nextRowNumber, Number(match[1]) + 1);
    }
  }
}

export function createCircuitRowDraft(): CircuitRowDraft {
  return {
    id: createCircuitRowId(),
    code: "",
    name: "",
    circuitKind: "",
    cableSource: "LINKED",
    cableRunId: "",
    lineConductorAreaMm2: "",
    protectiveConductorAreaMm2: "",
    conductorMaterial: "",
    routeLengthM: "",
    parallelRuns: "",
    protectiveConductorRuns: "",
    cableSourceText: "",
    lineResistanceOhmPerKm20c: "",
    protectiveResistanceOhmPerKm20c: "",
    resistanceSource: "",
    reactanceOhmPerKm: "",
    reactanceSource: "",
    conductorTemperatureC: "",
    temperatureSource: "",
    deviceCode: "",
    deviceKind: "",
    mcbType: "",
    ratedCurrentA: "",
    ratedVoltageV: "",
    characteristicText: "",
    requiredDisconnectionTimeS: "",
    timeSource: "",
    operatingCurrentA: "",
    operatingCurrentSource: "",
    maximumLoopImpedanceOhm: "",
    maximumLoopImpedanceSource: "",
    operatingCurrentBasis: "",
    minimumMarginPercent: "",
    sldElementCode: "",
  };
}

// Only the defaults the backend itself has are filled in: the IN profile. Ze starts on the
// linked earth-fault run (D3) with no run chosen yet; every choice starts blank; one empty
// circuit row waits for the engineer, its cable on the linked cable run (D5).
export function createInitialEarthFaultLoopDraft(): EarthFaultLoopDraft {
  return {
    code: "",
    name: "",
    busCode: "",
    systemKind: "",
    supplyKind: "",
    systemNominalVoltageV: "",
    frequencyHz: "",
    externalImpedanceSource: "LINKED",
    faultRunId: "",
    declaredExternalImpedanceOhm: "",
    declaredExternalImpedanceSource: "",
    minimumVoltageFactor: "",
    voltageFactorSource: "",
    nominalLineToEarthVoltageV: "",
    nominalVoltageSource: "",
    rows: [createCircuitRowDraft()],
    jurisdictionProfile: "IN",
    sldElementCode: "",
    notes: "",
  };
}

// An optional text or decimal field: sent trimmed, or left out when blank.
function optional(key: string, typed: string): Record<string, string> {
  const value = typed.trim();
  return value === "" ? {} : { [key]: value };
}

// An optional whole number (the parallel runs): sent as a number, or left out when blank.
function optionalInteger(key: string, typed: string): Record<string, number | string> {
  const value = typed.trim();
  if (value === "") {
    return {};
  }
  const parsed = Number(value);
  return { [key]: Number.isInteger(parsed) ? parsed : value };
}

// The device as the request carries it: identity and rating as typed (blank as "" so the
// schema names it), an unmade kind left out so the schema reports it, every declared item
// left out when blank (NOT ESTABLISHED, section 5).
function devicePayload(row: CircuitRowDraft): Record<string, unknown> {
  return {
    device_code: row.deviceCode.trim(),
    ...optional("device_kind", row.deviceKind),
    ...optional("mcb_type", row.mcbType),
    rated_current_a: row.ratedCurrentA.trim(),
    ...optional("rated_voltage_v", row.ratedVoltageV),
    ...optional("characteristic_text", row.characteristicText),
    ...optional("required_disconnection_time_s", row.requiredDisconnectionTimeS),
    ...optional("time_source", row.timeSource),
    ...optional("operating_current_a", row.operatingCurrentA),
    ...optional("operating_current_source", row.operatingCurrentSource),
    ...optional("maximum_loop_impedance_ohm", row.maximumLoopImpedanceOhm),
    ...optional("maximum_loop_impedance_source", row.maximumLoopImpedanceSource),
    ...optional("operating_current_basis", row.operatingCurrentBasis),
  };
}

// The cable as chosen on screen: the linked run's id alone, or the typed block alone (D5).
// A blank run id is sent as "" so the schema names it; the builder never sends both.
function cablePayload(row: CircuitRowDraft): Record<string, unknown> {
  if (row.cableSource === "TYPED") {
    return {
      cable: {
        line_conductor_area_mm2: row.lineConductorAreaMm2.trim(),
        ...optional("protective_conductor_area_mm2", row.protectiveConductorAreaMm2),
        ...optional("conductor_material", row.conductorMaterial),
        route_length_m: row.routeLengthM.trim(),
        ...optionalInteger("parallel_runs", row.parallelRuns),
        ...optionalInteger("protective_conductor_runs", row.protectiveConductorRuns),
        ...optional("source", row.cableSourceText),
      },
    };
  }
  return { cable_run_id: row.cableRunId.trim() };
}

// One circuit as the request carries it.
function rowPayload(row: CircuitRowDraft): Record<string, unknown> {
  return {
    code: row.code.trim(),
    name: row.name.trim(),
    ...optional("circuit_kind", row.circuitKind),
    ...cablePayload(row),
    ...optional("line_resistance_ohm_per_km_20c", row.lineResistanceOhmPerKm20c),
    ...optional("protective_resistance_ohm_per_km_20c", row.protectiveResistanceOhmPerKm20c),
    ...optional("resistance_source", row.resistanceSource),
    ...optional("reactance_ohm_per_km", row.reactanceOhmPerKm),
    ...optional("reactance_source", row.reactanceSource),
    ...optional("conductor_temperature_c", row.conductorTemperatureC),
    ...optional("temperature_source", row.temperatureSource),
    device: devicePayload(row),
    ...optional("minimum_margin_percent", row.minimumMarginPercent),
    ...optional("sld_element_code", row.sldElementCode),
  };
}

// Ze as chosen on screen: the linked run's id alone, the declared block alone, or nothing
// (D3). A blank run id or a blank declared figure is sent as "" so the schema names it.
function externalImpedancePayload(draft: EarthFaultLoopDraft): Record<string, unknown> {
  if (draft.externalImpedanceSource === "DECLARED") {
    return {
      external_impedance: {
        value_ohm: draft.declaredExternalImpedanceOhm.trim(),
        ...optional("source", draft.declaredExternalImpedanceSource),
      },
    };
  }
  if (draft.externalImpedanceSource === "NONE") {
    return {};
  }
  return { fault_run_id: draft.faultRunId.trim() };
}

function voltageFactorPayload(draft: EarthFaultLoopDraft): Record<string, unknown> {
  const value = draft.minimumVoltageFactor.trim();
  if (value === "") {
    return {};
  }
  return { minimum_voltage_factor: { value, ...optional("source", draft.voltageFactorSource) } };
}

// The request body as the form will send it. It is validated afterwards with
// earthFaultLoopRunCreateRequestSchema, so this function does not judge the values and never
// reorders the rows.
export function buildEarthFaultLoopRunPayload(
  draft: EarthFaultLoopDraft,
  projectRevisionId?: string,
): Record<string, unknown> {
  return {
    study: {
      code: draft.code.trim(),
      name: draft.name.trim(),
      bus_code: draft.busCode.trim(),
      ...optional("system_kind", draft.systemKind),
      ...optional("supply_kind", draft.supplyKind),
      system_nominal_voltage_v: draft.systemNominalVoltageV.trim(),
      frequency_hz: draft.frequencyHz.trim(),
      ...externalImpedancePayload(draft),
      ...voltageFactorPayload(draft),
      ...optional("nominal_line_to_earth_voltage_v", draft.nominalLineToEarthVoltageV),
      ...optional("nominal_voltage_source", draft.nominalVoltageSource),
      // Every row is sent, so the number in a message such as "Circuit 2 — Code is required."
      // always matches the row's position on screen.
      circuits: draft.rows.map(rowPayload),
      ...optional("jurisdiction_profile", draft.jurisdictionProfile),
      ...optional("sld_element_code", draft.sldElementCode),
    },
    ...(projectRevisionId === undefined ? {} : { project_revision_id: projectRevisionId }),
    ...optional("notes", draft.notes),
  };
}

// On-screen labels of the earth-fault loop form, keyed by the request path. The run request
// nests the study under "study", so every study path carries that prefix; describeLocation
// numbers the rows, giving "Circuit 2 — ...".
export const EARTH_FAULT_LOOP_LABELS: ValidationLabels = {
  fields: {
    "study.code": "Study code",
    "study.name": "Study name",
    "study.bus_code": "Bus code",
    "study.system_kind": "System earthing",
    "study.supply_kind": "Supply",
    "study.system_nominal_voltage_v": "System nominal voltage Un (V)",
    "study.frequency_hz": "Frequency (Hz)",
    "study.fault_run_id": "Earth-fault run",
    "study.external_impedance": "External loop impedance source",
    "study.external_impedance.value_ohm": "Declared Ze (Ω)",
    "study.external_impedance.source": "Ze source",
    "study.minimum_voltage_factor": "Minimum voltage factor",
    "study.minimum_voltage_factor.value": "Minimum voltage factor C_min",
    "study.minimum_voltage_factor.source": "Voltage factor source",
    "study.nominal_line_to_earth_voltage_v": "Declared nominal Uo (V)",
    "study.nominal_voltage_source": "Nominal Uo source",
    "study.circuits": "Circuits",
    "study.circuits.*.code": "Circuit code",
    "study.circuits.*.name": "Circuit name",
    "study.circuits.*.circuit_kind": "Circuit kind",
    "study.circuits.*.cable_run_id": "Cable run",
    "study.circuits.*.cable": "Cable figures",
    "study.circuits.*.cable.line_conductor_area_mm2": "Line conductor area S1 (mm²)",
    "study.circuits.*.cable.protective_conductor_area_mm2": "Protective conductor area S2 (mm²)",
    "study.circuits.*.cable.conductor_material": "Conductor material",
    "study.circuits.*.cable.route_length_m": "Route length (m)",
    "study.circuits.*.cable.parallel_runs": "Parallel runs",
    "study.circuits.*.cable.protective_conductor_runs": "Protective conductor runs",
    "study.circuits.*.cable.source": "Cable figures source",
    "study.circuits.*.line_resistance_ohm_per_km_20c": "Declared line resistance r20 (Ω/km)",
    "study.circuits.*.protective_resistance_ohm_per_km_20c":
      "Declared protective conductor resistance r20 (Ω/km)",
    "study.circuits.*.resistance_source": "Resistance source",
    "study.circuits.*.reactance_ohm_per_km": "Reactance per conductor (Ω/km)",
    "study.circuits.*.reactance_source": "Reactance source",
    "study.circuits.*.conductor_temperature_c": "Conductor temperature θ (°C)",
    "study.circuits.*.temperature_source": "Temperature source",
    "study.circuits.*.device": "Protective device",
    "study.circuits.*.device.device_code": "Device code",
    "study.circuits.*.device.device_kind": "Device kind",
    "study.circuits.*.device.mcb_type": "MCB type",
    "study.circuits.*.device.rated_current_a": "Rated current In (A)",
    "study.circuits.*.device.rated_voltage_v": "Rated voltage (V)",
    "study.circuits.*.device.characteristic_text": "Characteristic (as the data sheet names it)",
    "study.circuits.*.device.required_disconnection_time_s": "Required disconnection time (s)",
    "study.circuits.*.device.time_source": "Disconnection time source",
    "study.circuits.*.device.operating_current_a": "Operating current Ia (A)",
    "study.circuits.*.device.operating_current_source": "Operating current source",
    "study.circuits.*.device.maximum_loop_impedance_ohm": "Declared maximum Zs (Ω)",
    "study.circuits.*.device.maximum_loop_impedance_source": "Maximum Zs source",
    "study.circuits.*.device.operating_current_basis": "Operating current basis",
    "study.circuits.*.minimum_margin_percent": "Minimum margin (%)",
    "study.circuits.*.sld_element_code": "Feeder code",
    "study.jurisdiction_profile": "Jurisdiction profile",
    "study.sld_element_code": "Bus element code",
    notes: "Study notes",
  },
  // One row of the circuit table: "Circuit 2".
  groups: { circuits: "Circuit" },
};

// The words of every choice the form offers, typed against the contract enums so a value
// the contract gains cannot pass without a label.

export const SYSTEM_KIND_LABELS: Record<SystemKind, string> = {
  TN_S: "TN-S",
  TN_C_S: "TN-C-S",
  TN_C: "TN-C inside the installation (refused)",
  TT: "TT (not assessed in this version)",
  IT: "IT (not assessed in this version)",
};

export const SUPPLY_KIND_LABELS: Record<SupplyKind, string> = {
  THREE_PHASE_FOUR_WIRE: "Three-phase four-wire (U0 = Un / √3)",
  SINGLE_PHASE: "Single-phase (U0 = Un)",
};

export const CIRCUIT_KIND_LABELS: Record<CircuitKind, string> = {
  FINAL: "Final circuit",
  DISTRIBUTION: "Distribution circuit",
};

export const DEVICE_KIND_LABELS: Record<DeviceKind, string> = {
  MCB: "MCB",
  MCCB: "MCCB",
  ACB: "ACB",
  FUSE: "Fuse",
  RCD: "RCD (as the disconnecting device, Ia = IΔn)",
  OTHER: "Other",
};

export const MCB_TYPE_LABELS: Record<McbType, string> = {
  B: "Type B",
  C: "Type C",
  D: "Type D",
  NOT_APPLICABLE: "Not applicable (not an MCB)",
};

export const OPERATING_CURRENT_BASIS_LABELS: Record<OperatingCurrentBasis, string> = {
  TIME_CURRENT_DATA: "Manufacturer's time-current data",
  STANDARD_TABLE: "A standard's table (declared)",
  PROJECT_DESIGN_BASIS: "Project design basis",
  RCD_RATED_RESIDUAL_CURRENT: "RCD rated residual current IΔn",
};

export const EXTERNAL_IMPEDANCE_SOURCE_LABELS: Record<ExternalImpedanceSourceChoice, string> = {
  LINKED: "Linked earth-fault run (minimum case, single-phase to earth)",
  DECLARED: "Declared value with source (review required)",
  NONE: "Not established (nothing compared)",
};

export const CABLE_SOURCE_LABELS: Record<CableSourceChoice, string> = {
  LINKED: "Linked cable run",
  TYPED: "Typed figures with source",
};
